import React, { useState, useEffect, useRef } from 'react';

import { onAuthStateChanged, signInAnonymously, signInWithCustomToken, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp, onSnapshot, collection, query, where, deleteDoc } from 'firebase/firestore';
import { auth, db, storage, functions, MAPS_API_KEY } from './config/firebase';
import { ToastProvider, useToast } from './contexts/ToastContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CookieBanner from './components/layout/CookieBanner';
import BackToTop from './components/ui/BackToTop'; 
import SupportWidget from './components/shared/SupportWidget';
import AuthModal from './features/auth/AuthModal';
import ResetPasswordView from './features/auth/ResetPasswordView';
import GuestPhotoUpload from './features/evites/GuestPhotoUpload';
import Home from './pages/Home';
import AllCategoriesPage from './pages/AllCategoriesPage';
import Listings from './pages/Listings';
import EViteCreator from './features/evites/EViteCreator';
import RsvpPage from './features/evites/RsvpPage';
import UpdatedListingDetail from './pages/UpdatedListingDetail';
import VendorDashboard from './features/vendor/VendorDashboard';
import PlannerDashboard from './pages/PlannerDashboard';
import Messaging from './pages/Messaging';
import MyPage from './pages/MyPage';
import UpdatedSettings from './pages/UpdatedSettings';
import BookingDetail from './features/bookings/BookingDetail';
import AdminDashboard from './pages/AdminDashboard';
import WeddingDashboard from './pages/WeddingDashboard';
import WeddingSiteEditor from './pages/WeddingSiteEditor';
import WeddingSitePublicView from './pages/WeddingSitePublicView';
import ContactUs from './pages/ContactUs';
import FaqPage from './pages/FaqPage';
import HowItWorks from './pages/HowItWorks';
import AboutUs from './pages/AboutUs';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GuestBookingLookup from './pages/GuestBookingLookup';
import EViteShowcasePage from './pages/EViteShowcasePage';
import WeddingWebsiteShowcasePage from './pages/WeddingWebsiteShowcasePage';
import PricingTiersPage from './pages/PricingTiersPage';
import WeddingPlannerPro from './pages/WeddingPlannerPro';
import PlannerProPublicView from './pages/PlannerProPublicView';
import PlannerWebsitePublicView from './pages/PlannerWebsitePublicView';
import GlobalDashboard from './features/plannerpro/GlobalDashboard';
import ImageCropModal from './features/vendor/ImageCropModal';

export default function App() {
    // --- Intercept URL for Public Guest Photo Upload ---
    const urlParams = new URLSearchParams(window.location.search);
    const guestUploadEventId = urlParams.get('guestUpload');
    const guestUploadEventName = urlParams.get('eventName');

    if (guestUploadEventId) {
        return <GuestPhotoUpload eventId={guestUploadEventId} eventName={guestUploadEventName} db={db} storage={storage} />;
    }

    return (
        <ToastProvider>
            <MainApp />
        </ToastProvider>
    );
}

function MainApp() {
    const [user, setUser] = useState(null);
    const toast = useToast(); 
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [route, setRoute] = useState({ page: 'home', params: {} });
    
    const [preAuthPage, setPreAuthPage] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem('hostit_preAuthPage')) || { page: 'home', data: null }; }
        catch { return { page: 'home', data: null }; }
    });
    
    const [selectedListing, setSelectedListing] = useState(null);
    const [selectedChat, setSelectedChat] = useState(null);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedBooking, setSelectedBooking] = useState(null);
    
    const [searchFilters, setSearchFilters] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem('hostit_searchFilters')) || {}; }
        catch { return {}; }
    });
    
    const userDocListener = useRef(null);
    const [authModal, setAuthModal] = useState({ show: false, form: 'login' });
    const [isMapsScriptLoaded, setIsMapsScriptLoaded] = useState(false);
    const [selectedWedding, setSelectedWedding] = useState(null);
    const [isUserAuthenticated, setIsUserAuthenticated] = useState(false); 
    
    const [pageData, setPageData] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem('hostit_pageData')) || null; }
        catch { return null; }
    });
    
    const [isUpgrading, setIsUpgrading] = useState(false); 

    const [projects, setProjects] = useState([]);

    const [systemConfig, setSystemConfig] = useState(null);

    // Global Image Cropper State
    const [cropModalConfig, setCropModalConfig] = useState(null);

    useEffect(() => {
        const handleInteract = (e) => {
            if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
                // 1. Prevent iOS Safari zoom via viewport
                let viewportMeta = document.querySelector('meta[name="viewport"]');
                if (!viewportMeta) {
                    viewportMeta = document.createElement('meta');
                    viewportMeta.name = 'viewport';
                    document.head.appendChild(viewportMeta);
                }
                viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0');
                
                // 2. Prevent Chrome Android zoom by temporarily forcing 16px font size
                const currentSize = window.getComputedStyle(e.target).fontSize;
                if (parseFloat(currentSize) < 16) {
                    e.target.dataset.zoomed = 'true';
                    e.target.style.setProperty('font-size', '16px', 'important');
                }
            }
        };

        const handleFocusIn = (e) => {
            if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
                // Ensure field stays visible above the keyboard
                setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
            }
        };

        const handleFocusOut = (e) => {
            if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
                let viewportMeta = document.querySelector('meta[name="viewport"]');
                if (viewportMeta) {
                    // Restore pinch-to-zoom accessibility
                    setTimeout(() => {
                        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1');
                    }, 300);
                }
                
                // Remove temporary font-size override to restore original design
                if (e.target.dataset.zoomed === 'true') {
                    e.target.style.removeProperty('font-size');
                    delete e.target.dataset.zoomed;
                }
            }
        };

        document.addEventListener('touchstart', handleInteract, { passive: true });
        document.addEventListener('mousedown', handleInteract, { passive: true });
        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);
        
        return () => {
            document.removeEventListener('touchstart', handleInteract);
            document.removeEventListener('mousedown', handleInteract);
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, []);

    // --- Persist state to session storage ---
    useEffect(() => {
        sessionStorage.setItem('hostit_preAuthPage', JSON.stringify(preAuthPage));
    }, [preAuthPage]);

    useEffect(() => {
        sessionStorage.setItem('hostit_searchFilters', JSON.stringify(searchFilters));
    }, [searchFilters]);

    useEffect(() => {
        if (pageData) sessionStorage.setItem('hostit_pageData', JSON.stringify(pageData));
        else sessionStorage.removeItem('hostit_pageData');
    }, [pageData]);

    // --- Load External Scripts and Styles ---
    useEffect(() => {
        if (!window.React) {
            window.React = React;
        }

        const scripts = [
            { id: "google-maps-script", src: `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=places`, onLoad: () => setIsMapsScriptLoaded(true) },
            { id: "cropperjs-script", src: "https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.5.12/cropper.min.js" },
            { id: "stripe-v3-script", src: "https://js.stripe.com/v3/" },
            { id: "confetti-script", src: "https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js" }
        ];
        
        const styles = [
            { id: "cropperjs-style", href: "https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.5.12/cropper.min.css" }
        ];

        if (!document.querySelector("link[rel*='icon']")) {
            const link = document.createElement('link');
            link.type = 'image/png';
            link.rel = 'shortcut icon';
            link.href = '/favicon.png';
            document.getElementsByTagName('head')[0].appendChild(link);
        }

        scripts.forEach(scriptInfo => {
            if (!document.getElementById(scriptInfo.id)) {
                const script = document.createElement('script');
                script.id = scriptInfo.id;
                script.src = scriptInfo.src;
                script.async = true;
                script.defer = true;
                if (scriptInfo.onLoad) script.onload = scriptInfo.onLoad;
                document.head.appendChild(script);
            } else if (scriptInfo.id === 'google-maps-script' && !isMapsScriptLoaded) {
                setIsMapsScriptLoaded(true);
            }
        });

        styles.forEach(styleInfo => {
            if (!document.getElementById(styleInfo.id)) {
                const link = document.createElement('link');
                link.id = styleInfo.id;
                link.rel = "stylesheet";
                link.href = styleInfo.href;
                document.head.appendChild(link);
            }
        });
    }, [isMapsScriptLoaded]);

    // Fetch System Config
    useEffect(() => {
        const unsubConfig = onSnapshot(doc(db, 'system', 'config'), (doc) => {
            if (doc.exists()) setSystemConfig(doc.data());
        }, (error) => {
             if (error.code !== 'permission-denied') console.error("Config fetch error", error);
        });
        return () => unsubConfig();
    }, []);

    // --- Authentication and Wedding Site Routing ---
    useEffect(() => {
        const handleHashChange = () => {
            const fullPath = window.location.hash.substring(1);
            if (!fullPath) {
                setRoute({ page: 'home', params: {} });
                return;
            }
            
            const [path, queryString = ''] = fullPath.split('?');
            const parts = path.split('/');
            const page = parts[0];
            const params = {};
            if (queryString) {
                const queryParams = new URLSearchParams(queryString);
                queryParams.forEach((value, key) => {
                    params[key] = value;
                });
            }
            if (parts.length > 1) {
                params.id = parts[1]; 
                if (page === 'weddingSitePublicView') params.subdomain = parts[1];
            }
            setRoute({ page, params });
        };

        const path = window.location.pathname;
        if (path.startsWith('/w/')) {
            const subdomain = path.split('/w/')[1];
            if (subdomain) {
                setRoute({ page: 'weddingSitePublicView', params: { subdomain } });
            }
        } else {
            window.addEventListener('hashchange', handleHashChange);
            handleHashChange(); 
        }

        const initAuth = async () => {
            try {
                await auth.authStateReady(); 
                if (typeof window.__initial_auth_token !== 'undefined' && window.__initial_auth_token) {
                    try {
                        await signInWithCustomToken(auth, window.__initial_auth_token);
                    } catch (tokenErr) {
                        console.warn("Custom token failed, falling back to anonymous session:", tokenErr);
                        if (!auth.currentUser) await signInAnonymously(auth);
                    }
                } else if (!auth.currentUser) {
                    await signInAnonymously(auth);
                }
            } catch (err) {
                console.error("Auth init error:", err);
            }
        };
        initAuth();

        const authUnsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (userDocListener.current) userDocListener.current();

            if (currentUser) {
                if (currentUser.isAnonymous) {
                    setUser(currentUser);
                    setIsUserAuthenticated(false);
                    setIsAuthReady(true);
                } else {
                    setIsAuthReady(false);
                    const userDocRef = doc(db, "users", currentUser.uid);
                    userDocListener.current = onSnapshot(userDocRef, (docSnap) => {
                        if (docSnap.exists()) {
                            setUser({ ...docSnap.data(), uid: currentUser.uid, email: currentUser.email, isAnonymous: false });
                            setIsUserAuthenticated(true); 
                            setIsAuthReady(true); 
                        } else {
                            setUser({ uid: currentUser.uid, email: currentUser.email, isAnonymous: false });
                            setIsUserAuthenticated(true);
                            setIsAuthReady(true);
                        }
                    }, (error) => {
                        console.error("Error fetching user document:", error);
                        setIsUserAuthenticated(false);
                        setIsAuthReady(true);
                    });
                }
            } else {
                setUser(null);
                setIsUserAuthenticated(false);
                setIsAuthReady(true);
                signInAnonymously(auth).catch(err => console.error("Guest session restore failed:", err));
            }
        });

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            authUnsubscribe();
            if (userDocListener.current) userDocListener.current();
        };
    }, []);

    useEffect(() => {
        const fetchAndSetData = async () => {
            const { page, params } = route;
            if (params.id) {
                if (page === 'listingDetail' && (!selectedListing || selectedListing.id !== params.id)) {
                    const docRef = doc(db, 'listings', params.id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) setSelectedListing({ id: docSnap.id, ...docSnap.data() });
                    else window.location.hash = '#home';
                } else if (page === 'bookingDetail' && (!selectedBooking || selectedBooking.id !== params.id)) {
                    if (pageData?.booking && String(pageData.booking.id) === String(params.id)) {
                        setSelectedBooking(pageData.booking);
                    } 
                    else if (user && !user.isAnonymous) {
                        const docRef = doc(db, 'bookings', params.id);
                        const docSnap = await getDoc(docRef);
                        if (docSnap.exists()) {
                            setSelectedBooking({ id: docSnap.id, ...docSnap.data() });
                        } else {
                            window.location.hash = '#dashboard'; 
                        }
                    } 
                    else if (user && user.isAnonymous) {
                        window.location.hash = '#guestBookingLookup';
                    }
                } else if (page === 'vendorProfile' && (!selectedVendor || selectedVendor.vendorId !== params.id)) {
                    setSelectedVendor({ vendorId: params.id });
                } else if (page === 'weddingSiteEditor' && (!selectedWedding || selectedWedding.weddingId !== params.id)) {
                    setSelectedWedding({ weddingId: params.id });
                }
            } else {
                setSelectedListing(null);
                setSelectedBooking(null);
                setSelectedVendor(null);
                setSelectedWedding(null);
            }
        };
        if (db && isAuthReady && route.page !== 'weddingSitePublicView') {
            fetchAndSetData();
        }
    }, [route, db, isAuthReady, pageData, user]);

    // Fetch projects for Global Dashboard
    useEffect(() => {
        if (!user || user.isAnonymous || !db) {
            setProjects([]);
            return;
        }

        const q = query(collection(db, "plannerProjects"), where("plannerId", "==", user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const projectsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setProjects(projectsData);
        });

        return () => unsubscribe();
    }, [user, db]);

    // Global Dashboard handlers
    const handleSelectProject = (projectId) => {
        navigateTo('plannerPro', { projectId });
    };

    const handleCreateProject = () => {
        navigateTo('plannerPro');
    };

    const handleDeleteProject = async (projectId) => {
        if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
            return;
        }
        
        try {
            await deleteDoc(doc(db, "plannerProjects", projectId));
            toast.success('Project deleted successfully');
        } catch (error) {
            console.error('Error deleting project:', error);
            toast.error('Failed to delete project');
        }
    };

    const navigateTo = (pageName, data = null) => {
        if (pageName === 'login' || pageName === 'signup') {
            let contextData = data;
            if (!contextData) {
                if (route.page === 'listingDetail' && selectedListing) contextData = selectedListing;
                else if (route.page === 'bookingDetail' && selectedBooking) contextData = { booking: selectedBooking };
                else if (route.page === 'vendorProfile' && selectedVendor) contextData = selectedVendor;
                else if (route.page === 'weddingSiteEditor' && selectedWedding) contextData = { weddingId: selectedWedding.weddingId };
            }
            setPreAuthPage({ page: route.page, data: contextData });
            setAuthModal({ show: true, form: pageName });
            return;
        }

        if (pageName === 'admin' && (!user || user.isAnonymous)) {
            setPreAuthPage({ page: 'admin', data: null });
            setAuthModal({ show: true, form: 'login' });
            return;
        }

        setPageData(data);

        let path = pageName;
        if (pageName === 'listingDetail' && data?.id) {
            path = `listingDetail/${data.id}`;
        } else if (pageName === 'bookingDetail' && data?.booking?.id) {
            path = `bookingDetail/${data.booking.id}`;
        } else if (pageName === 'vendorProfile' && data?.vendorId) {
            path = `vendorProfile/${data.vendorId}`;
        } else if (pageName === 'messages' && data?.chatId) {
            path = `messages/${data.chatId}`;
        } else if (pageName === 'listings') {
            setSearchFilters(data || {});
            path = 'listings';
        } else if (pageName === 'weddingSiteEditor' && data?.weddingId) {
            path = `weddingSiteEditor/${data?.weddingId}`;
        } else if (pageName === 'evite' && data?.id) {
             path = `evite/${data.id}`;
        } else if (pageName === 'plannerProShared' && data?.id) {
             path = `plannerProShared/${data.id}`;
        } else if (pageName === 'planner' && data?.id) {
             path = `planner/${data.id}`;
        }

        window.location.hash = path;
        window.scrollTo(0, 0);
    };

    const closeAuthModal = () => setAuthModal({ show: false, form: 'login' });

    const becomeVendor = async () => {
        if (isUpgrading) return;
        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.isAnonymous) {
            toast.error("Please log in with a permanent account to become a vendor.");
            setPreAuthPage({ page: 'settings', data: null }); 
            navigateTo('signup');
            return;
        }

        setIsUpgrading(true);
        try {
            const userDocRef = doc(db, "users", currentUser.uid);
            await updateDoc(userDocRef, { 
                isVendor: true,
                vendorSince: serverTimestamp()
            });
            setUser(prev => ({ ...prev, isVendor: true }));
            toast.success("Vendor features unlocked! Welcome aboard.");
            navigateTo('dashboard');
        } catch (error) {
            console.error("Vendor upgrade failed:", error);
            toast.error(`Upgrade failed: ${error.message}`);
        } finally {
            setIsUpgrading(false);
        }
    };

    if (!isAuthReady) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mb-4"></div>
                <div className="text-xl font-bold text-gray-700 italic tracking-tight">Host-it!</div>
                <p className="text-gray-400 text-sm mt-2">Preparing your party...</p>
            </div>
        );
    }

    const isPublicWeddingSite = route.page === 'weddingSitePublicView';
    const hideFooterPages = ['weddingSitePublicView', 'messages', 'admin', 'plannerPro', 'weddingSiteEditor', 'planner'];
    const showFooter = !hideFooterPages.includes(route.page);
    const hideHeaderPages = ['weddingSitePublicView', 'myPage', 'vendorProfile', 'plannerPro', 'planner'];
    const showHeader = !hideHeaderPages.includes(route.page);

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <CookieBanner />
            
            {window.location.search.includes('mode=resetPassword') && (
                <ResetPasswordView auth={auth} />
            )}

            {/* Global Image Crop Modal */}
            {cropModalConfig && cropModalConfig.file && (
                <ImageCropModal
                    file={cropModalConfig.file}
                    aspectRatio={cropModalConfig.aspectRatio || 1}
                    onClose={() => setCropModalConfig(null)}
                    onCrop={(croppedFile) => {
                        if (cropModalConfig.onCrop) cropModalConfig.onCrop(croppedFile);
                        setCropModalConfig(null); // Close modal automatically after crop
                    }}
                />
            )}

            {systemConfig?.announcement && (
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-center py-2 px-4 text-sm font-bold shadow-md z-[60] animate-fade-in-down">
                    {systemConfig.announcement}
                </div>
            )}

            {authModal.show && <AuthModal initialForm={authModal.form} onClose={closeAuthModal} navigateTo={navigateTo} preAuthPageInfo={preAuthPage} />}

            {showHeader && <Header user={user} navigateTo={navigateTo} />}

            <main className={
                isPublicWeddingSite ? 'flex-grow' : 
                route.page === 'plannerPro' ? 'flex-grow flex flex-col min-h-0 overflow-hidden' :
                'p-4 md:p-8 flex-grow'
            }>
                 {(() => {
                    switch (route.page) {
                        case 'home':
                            return <Home user={user} navigateTo={navigateTo} isMapsScriptLoaded={isMapsScriptLoaded} isAuthReady={isAuthReady} db={db} />;
                        case 'allCategories':
                            return <AllCategoriesPage navigateTo={navigateTo} />;
                        case 'listings':
                            return <Listings user={user} navigateTo={navigateTo} filters={searchFilters} isMapsScriptLoaded={isMapsScriptLoaded} isAuthReady={isAuthReady} db={db} />;
                        case 'evite':
                            return <EViteCreator user={user} navigateTo={navigateTo} db={db} storage={storage} eventId={route.params.id} isMapsScriptLoaded={isMapsScriptLoaded} />;
                        case 'rsvp':
                            return route.params.id && <RsvpPage inviteId={route.params.id} db={db} functions={functions} />;
                        case 'listingDetail':
                            return selectedListing && <UpdatedListingDetail listing={selectedListing} user={user} db={db} functions={functions} navigateTo={navigateTo} isMapsScriptLoaded={isMapsScriptLoaded} isAuthReady={isAuthReady} isUserAuthenticated={isUserAuthenticated} />;
                        case 'dashboard':
                            return user && !user.isAnonymous && (
                                user.isVendor ? 
                                <VendorDashboard user={user} db={db} storage={storage} functions={functions} navigateTo={navigateTo} initialData={pageData} isMapsScriptLoaded={isMapsScriptLoaded} isUserAuthenticated={isUserAuthenticated}/> 
                                : 
                                <GlobalDashboard 
                                    projects={projects} 
                                    onSelectProject={handleSelectProject} 
                                    onCreateProject={handleCreateProject} 
                                    onDeleteProject={handleDeleteProject} 
                                />
                            );
                        case 'globalDashboard':
                            return user && !user.isAnonymous && <GlobalDashboard 
                                projects={projects} 
                                onSelectProject={handleSelectProject} 
                                onCreateProject={handleCreateProject} 
                                onDeleteProject={handleDeleteProject} 
                            />;
                        case 'plannerDashboard':
                            return user && !user.isAnonymous && <PlannerDashboard user={user} navigateTo={navigateTo} db={db} />;
                        case 'messages':
                             return user && !user.isAnonymous && <Messaging user={user} initialChatId={route.params.id} navigateTo={navigateTo} initialMessage={pageData?.prefillMessage} db={db} storage={storage} />;
                        case 'vendorProfile':
                            return selectedVendor && <MyPage user={user} vendorId={selectedVendor.vendorId} navigateTo={navigateTo} isPublicView={true} db={db} storage={storage} />;
                        case 'myPage':
                            return user && !user.isAnonymous && user.isVendor && <MyPage user={user} navigateTo={navigateTo} isPublicView={false} db={db} storage={storage} />;
                        case 'settings':
                            return user && !user.isAnonymous && <UpdatedSettings user={user} becomeVendor={becomeVendor} db={db} functions={functions} isUpgrading={isUpgrading} navigateTo={navigateTo} />;
                        case 'bookingDetail':
                            return selectedBooking && <BookingDetail booking={selectedBooking} user={user} db={db} storage={storage} functions={functions} navigateTo={navigateTo} fromPage={pageData?.from} fromDate={pageData?.selectedDate} />;
                        case 'admin':
                            if (!user || user.isAnonymous) {
                                return (
                                    <div className="container mx-auto max-w-md py-16 px-4">
                                        <div className="bg-white p-8 rounded-xl shadow-lg text-center border border-gray-100">
                                            <h2 className="text-2xl font-bold text-gray-800 mb-3">Admin Access Required</h2>
                                            <p className="text-gray-600 mb-8">Please log in to access the administration panel.</p>
                                            <button onClick={() => navigateTo('login')} className="w-full bg-pink-600 text-white py-3 rounded-lg font-semibold hover:bg-pink-700 transition">Log In</button>
                                        </div>
                                    </div>
                                );
                            }

                            if (!user.isAdmin) {
                                return (
                                    <div className="container mx-auto max-w-md py-16 px-4">
                                        <div className="bg-white p-8 rounded-xl shadow-lg text-center border border-gray-100">
                                            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                            </div>
                                            <h2 className="text-2xl font-bold text-gray-800 mb-3">Access Denied</h2>
                                            <p className="text-gray-600 mb-8">You do not have administrative privileges to view this area.</p>
                                            <button onClick={() => navigateTo('home')} className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition">Return Home</button>
                                        </div>
                                    </div>
                                );
                            }
                            return <AdminDashboard db={db} auth={auth} onExit={() => navigateTo('home')} navigateTo={navigateTo} />;
                        case 'weddingDashboard':
                            return user && <WeddingDashboard user={user} navigateTo={navigateTo} db={db} />;
                        case 'weddingSiteEditor':
                            return selectedWedding && <WeddingSiteEditor wedding={selectedWedding} user={user} db={db} navigateTo={navigateTo} />;
                        case 'weddingSitePublicView':
                            return route.params.subdomain && <WeddingSitePublicView subdomain={route.params.subdomain} isAuthReady={isAuthReady} />;
                        case 'contact':
                            return <ContactUs />;
                        case 'faq':
                            return <FaqPage />;
                        case 'how-it-works':
                            return <HowItWorks />;
                        case 'about':
                            return <AboutUs />;
                        case 'terms-of-service':
                            return <TermsOfService />;
                        case 'privacy-policy':
                            return <PrivacyPolicy />;
                        case 'guestBookingLookup':
                            return <GuestBookingLookup db={db} functions={functions} navigateTo={navigateTo} initialBookingNumber={route.params.bookingNumber} initialEmail={route.params.email} />;
                        case 'evite-showcase':
                            return <EViteShowcasePage navigateTo={navigateTo} user={user} />;
                        case 'wedding-showcase':
                            return <WeddingWebsiteShowcasePage navigateTo={navigateTo} user={user} />;
                        case 'pricing':
                            return <PricingTiersPage navigateTo={navigateTo} user={user} />;
                        case 'plannerPro':
                            return user && !user.isAnonymous ? (
                                <WeddingPlannerPro user={user} db={db} storage={storage} navigateTo={navigateTo} isMapsScriptLoaded={isMapsScriptLoaded} functions={functions} isUserAuthenticated={isUserAuthenticated} initialData={pageData} />
                            ) : (
                                <div className="container mx-auto py-32 text-center max-w-md">
                                    <div className="text-4xl mb-4">✨</div>
                                    <h2 className="text-3xl font-bold mb-4 font-serif text-gray-900">Host-it! Pro</h2>
                                    <p className="text-gray-600 mb-8">Please log in to your account to access the professional event planning dashboard.</p>
                                    <button onClick={() => navigateTo('login')} className="w-full bg-pink-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-pink-700 transition">Log In to Pro</button>
                                </div>
                            );
                        case 'plannerProShared':
                            return route.params.id && <PlannerProPublicView projectId={route.params.id} db={db} />;
                        case 'planner':
                            return route.params.id && <PlannerWebsitePublicView plannerId={route.params.id} db={db} navigateTo={navigateTo} user={user} />;
                        default:
                             return <Home user={user} navigateTo={navigateTo} isMapsScriptLoaded={isMapsScriptLoaded} isAuthReady={isAuthReady} db={db} />;
                    }
                })()}
            </main>
            
            {route.page !== 'admin' && <SupportWidget db={db} user={user} />}
            {showFooter && <Footer navigateTo={navigateTo} />}
            <BackToTop />
        </div>
    );
}